import React, { useState } from "react";
import { emitToast } from "./Toast";
import DatePickerCalendar from "./DatePickerCalendar";
import { HISTORY_MONTHS_BACK, diffMin, fmtMin, monthsAgoYm } from "./dateUtil";
import ErrorBar from "./ErrorBar";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// 残業 申請フォーム本体（社員本人・セッション認証）。Porcelain テーマ。
// 申請ハブ(ApplyHubPage)のタブに載る部品として OvertimeBody を named export する。
// 申請履歴は種別横断の共通一覧(ApplyHistory)へ集約したため、ここは「新規申請フォーム」だけを持つ。
// 送信に成功したら onChanged() を呼び、親（ApplyHubPage）に共通の申請履歴を再取得させる。
// 経営者の承認は別ルート(/employee/overtime-approval)。旧 /employee/overtime はハブへリダイレクト。
// 「この日は残業します」を事前・事後どちらでも登録する（＝申請）。開始/終了から残業時間を自動計算。
// 登録した残業は経営者の承認リストに載る（休み申請・時間訂正と同じ申請フロー）。
// ※ 固定みなし残業や勤務時間からの自動計算は行わない（手動登録のみ）。
const API_URL = process.env.REACT_APP_API_URL || "";

const EMPTY_FORM = { date: "", start: "", end: "", reason: "" };

export function OvertimeBody({ onChanged }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const minYm = monthsAgoYm(HISTORY_MONTHS_BACK);
  const overtimeMin = diffMin(form.start, form.end);

  const submit = async () => {
    if (!form.date) { setError("日付を選択してください。"); return; }
    if (!form.start || !form.end) { setError("開始と終了の時刻を入力してください。"); return; }
    if (diffMin(form.start, form.end) <= 0) { setError("終了は開始より後の時刻にしてください。"); return; }
    setSubmitting(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/overtime/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ date: form.date, start: form.start, end: form.end, reason: form.reason }),
      });
      if (!res.ok) { setError(await apiErrorMessage(res, "残業の申請に失敗しました。")); return; }
      setForm(EMPTY_FORM);
      emitToast("残業を申請しました。承認をお待ちください。");
      if (onChanged) onChanged();
    } catch (e) {
      setError("残業の申請に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      <h2 className="mp-h2">新規申請</h2>
      <div className="mp-panel" style={{ marginBottom: 28 }}>
        {/* 日付（カレンダー型）＋開始・終了・残業時間（自動計算）を横並び。スマホでは折り返し。 */}
        <div className="mp-formrow mp-ot-formrow">
          <div className="mp-field mp-ot-date">
            <label className="mp-flabel">日付</label>
            <DatePickerCalendar
              value={form.date}
              onChange={(ds) => setForm((p) => ({ ...p, date: ds }))}
              isDisabled={() => false}
              minYm={minYm}
              initialYm={monthsAgoYm(0)}
              placeholder="日付を選ぶ"
            />
          </div>
          <div className="mp-field mp-ot-time">
            <label className="mp-flabel">開始</label>
            <input className="mp-input" type="time" value={form.start} onChange={(e) => setForm((p) => ({ ...p, start: e.target.value }))} />
          </div>
          <div className="mp-field mp-ot-time">
            <label className="mp-flabel">終了</label>
            <input className="mp-input" type="time" value={form.end} onChange={(e) => setForm((p) => ({ ...p, end: e.target.value }))} />
          </div>
          <div className="mp-field mp-ot-time">
            <label className="mp-flabel">残業時間</label>
            <div className="mp-ot-calc">{fmtMin(overtimeMin)}</div>
          </div>
        </div>
        <div className="mp-field">
          <label className="mp-flabel">理由</label>
          <textarea className="mp-textarea" rows={2} placeholder="残業の理由（任意）" value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />
        </div>
        <button className="mp-btn mp-primary" disabled={submitting || !form.date || overtimeMin <= 0} onClick={submit}>{submitting ? "送信中…" : "申請する"}</button>
      </div>
    </>
  );
}
